import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { COLLECTIONS, OCCASIONS, OCCASION_LABEL } from "@/lib/content";
import { ProductGrid } from "@/components/home/Sections";

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const COLORS = ["Black", "Ivory", "Multicolour", "Red", "Blue", "Navy", "Pink", "Peach", "Sky Blue", "Mustard", "Bottle Green", "Wine", "Yellow"];
const FABRICS = ["Cotton", "Cotton Blend", "Rayon", "Georgette", "Organza", "Silk Blend"];
const PRICES = [["", "199", "Under ₹199"], ["200", "249", "₹200 – ₹249"], ["250", "299", "₹250 – ₹299"]];
const SORTS = [["popularity", "Popularity"], ["newest", "Newest"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["rating", "Customer Rating"]];
const LIST_KEYS = ["sizes", "colors", "fabrics", "occasions"];

const Group = ({ title, children }) => (
  <div className="border-b border-[#E7E2DB] py-5">
    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#57534E]">{title}</p>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
const Chip = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${active ? "border-[#701A24] bg-[#701A24] text-white" : "border-[#E7E2DB] bg-white hover:border-[#B85D43]"}`}>{children}</button>
);

export default function Collection() {
  const { handle } = useParams();
  const [sp, setSp] = useSearchParams();
  const meta = COLLECTIONS[handle] || COLLECTIONS.all;
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  useSeo(meta.title === "All Styles" ? "Shop Ethnic Wear for Women" : `${meta.title} for Women`, meta.desc);

  const list = (k) => (sp.get(k) ? sp.get(k).split(",") : []);
  const sort = sp.get("sort") || meta.params.sort || "popularity";
  const toggle = (k, v) => {
    const cur = list(k);
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    const n = new URLSearchParams(sp);
    next.length ? n.set(k, next.join(",")) : n.delete(k);
    setSp(n, { replace: true });
  };
  const setPrice = (min, max) => {
    const n = new URLSearchParams(sp);
    const same = (sp.get("min_price") || "") === min && (sp.get("max_price") || "") === max;
    ["min_price", "max_price"].forEach((k) => n.delete(k));
    if (!same) { min && n.set("min_price", min); max && n.set("max_price", max); }
    setSp(n, { replace: true });
  };
  const set = (k, v) => { const n = new URLSearchParams(sp); n.set(k, v); setSp(n, { replace: true }); };
  const qs = sp.toString();

  useEffect(() => {
    setItems(null);
    const params = { ...meta.params, ...Object.fromEntries(new URLSearchParams(qs)), sort };
    api.get("/products", { params }).then((r) => setItems(r.data));
  }, [handle, qs, sort, meta.params]);

  const activeCount = useMemo(() => LIST_KEYS.reduce((s, k) => s + list(k).length, 0) + (sp.get("min_price") || sp.get("max_price") ? 1 : 0), [qs]); // eslint-disable-line

  const filters = (
    <div data-testid="filters-panel">
      <Group title="Size">{SIZES.map((s) => <Chip key={s} testid={`filter-size-${s}`} active={list("sizes").includes(s)} onClick={() => toggle("sizes", s)}>{s}</Chip>)}</Group>
      <Group title="Colour">{COLORS.map((c) => <Chip key={c} testid={`filter-color-${c.toLowerCase().replace(/\s+/g, "-")}`} active={list("colors").includes(c)} onClick={() => toggle("colors", c)}>{c}</Chip>)}</Group>
      <Group title="Fabric">{FABRICS.map((f) => <Chip key={f} testid={`filter-fabric-${f.toLowerCase().replace(/\s+/g, "-")}`} active={list("fabrics").includes(f)} onClick={() => toggle("fabrics", f)}>{f}</Chip>)}</Group>
      <Group title="Price">{PRICES.map(([mn, mx, l], i) => <Chip key={l} testid={`filter-price-${i}`} active={(sp.get("min_price") || "") === mn && (sp.get("max_price") || "") === mx} onClick={() => setPrice(mn, mx)}>{l}</Chip>)}</Group>
      <Group title="Occasion">{OCCASIONS.map((o) => <Chip key={o.key} testid={`filter-occasion-${o.key}`} active={list("occasions").includes(o.key)} onClick={() => toggle("occasions", o.key)}>{o.name}</Chip>)}</Group>
      {activeCount > 0 && <button data-testid="clear-filters-button" onClick={() => setSp(sp.get("q") ? { q: sp.get("q") } : {}, { replace: true })} className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#B85D43]"><X className="h-3.5 w-3.5" /> Clear all filters</button>}
    </div>
  );

  return (
    <div data-testid="collection-page" className="kt-container py-10 sm:py-14">
      <nav className="text-xs text-[#78716C]"><Link to="/">Home</Link> / <span className="text-[#1C1917]">{meta.title}</span></nav>
      <div className="mt-4 max-w-3xl">
        <h1 data-testid="collection-title" className="font-serif-display text-5xl font-semibold sm:text-6xl">{sp.get("q") ? `Results for “${sp.get("q")}”` : list("occasions").length === 1 ? OCCASION_LABEL[list("occasions")[0]] : meta.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-[#57534E] sm:text-base">{meta.desc}</p>
      </div>
      <div className="sticky top-[72px] z-30 -mx-4 mt-8 flex items-center justify-between gap-3 border-y border-[#E7E2DB] bg-[#FAF7F2]/95 px-4 py-3 backdrop-blur-md sm:mx-0 sm:rounded-full sm:border sm:px-5">
        <button data-testid="open-filters-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm font-medium lg:hidden"><SlidersHorizontal className="h-4 w-4" /> Filters {activeCount > 0 && `(${activeCount})`}</button>
        <p data-testid="collection-count" className="hidden text-sm text-[#57534E] lg:block">{items ? `${items.length} styles` : "Loading…"}</p>
        <label className="flex items-center gap-2 text-sm">
          <span className="hidden text-[#78716C] sm:inline">Sort by</span>
          <select data-testid="sort-select" value={sort} onChange={(e) => set("sort", e.target.value)} className="rounded-full border border-[#E7E2DB] bg-white px-4 py-2 text-sm outline-none">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-8 grid gap-10 lg:grid-cols-12">
        <aside className="hidden lg:col-span-3 lg:block">{filters}</aside>
        <div className="lg:col-span-9">
          {items === null ? (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/5] animate-pulse rounded-2xl bg-[#EAE0D5]" />)}</div>
          ) : items.length === 0 ? (
            <div data-testid="no-results" className="rounded-3xl border border-dashed border-[#E7E2DB] p-14 text-center">
              <p className="font-serif-display text-3xl">No styles match yet</p>
              <p className="mt-2 text-sm text-[#78716C]">Try removing a filter or explore our best sellers.</p>
              <Link to="/collections/best-sellers" className="btn-primary mt-6">Shop Best Sellers</Link>
            </div>
          ) : (
            <div className="[&>div]:lg:grid-cols-3"><ProductGrid products={items} testid="collection-grid" /></div>
          )}
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[88%] overflow-y-auto bg-[#FAF7F2]">
          <SheetTitle className="font-serif-display text-2xl">Filters</SheetTitle>
          {filters}
          <button data-testid="apply-filters-button" onClick={() => setOpen(false)} className="btn-primary mt-6 w-full">Show {items?.length ?? ""} styles</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
